import { CREATE_NEW_GROWER, CREATE_NEW_SALESPERSON } from "../../../constants";
import { pruneEmptyGeometryFeatures } from "@domain/Geo/geometry";
import type { Job } from "../types";

/** Sentinel value used by the deal selector to indicate "custom configuration"
 *  — meaning the user filled in deal fields manually rather than picking an
 *  existing deal. Cleared on submit so the API doesn't try to look up a deal
 *  with id "custom". */
const CUSTOM_DEAL_SENTINEL = "custom";

/** Prefix on `importGrowerName` when the user selected "Create new grower"
 *  and typed a name. Stripped on submit so the API receives the bare name. */
const NEW_GROWER_NAME_PREFIX = "New Grower: ";

/**
 * Pure parse of a comma-separated micros-list string: trims each token, keeps
 * only alphanumeric tokens, dedupes (first-occurrence wins), rejoins with
 * commas. Returns undefined when the input is null/undefined/empty.
 *
 * Used by the SubmitJobs flows; the same shape exists as a local helper in
 * SingleImportForm and a similar inline parser in JobDetailModal.
 */
export function parseMicrosList(raw: string | undefined): string | undefined {
	if (!raw) return undefined;
	const seen = new Set<string>();
	const out: string[] = [];
	for (const part of raw.split(",")) {
		const token = part.trim();
		if (!token) continue;
		if (!/^[a-zA-Z0-9]+$/.test(token)) continue;
		if (seen.has(token)) continue;
		seen.add(token);
		out.push(token);
	}
	return out.join(",");
}

/**
 * Cleans the four user-facing sentinel values from a job before submission:
 *
 *   1. dealIds[0] === "custom"            → dealIds = []
 *   2. growerIds[0] === CREATE_NEW_GROWER → growerIds = []
 *   3. importGrowerName starts with       → strip the "New Grower: " prefix
 *   4. salespersonIds includes CREATE_NEW_SALESPERSON → salespersonIds = []
 *
 * The sentinels exist so the form UI can encode "the user wants to create a
 * new X" without inventing a separate flag — the API never sees them.
 *
 * Returns a new job; the input is not mutated. Callers handle their own
 * micros-list parsing (sources differ — see parseMicrosList).
 *
 * Accepts `Partial<Job>` so the submit pipeline (which threads `Partial<Job>`)
 * can run it as the final step before create. All four cleanups are optional-
 * chained, so a partial job is safe.
 */
/**
 * Import-time readiness: a job flagged "ready to sample" at submission is just
 * a job marked ready as of submission. Stamp `dateMarkedReady` (and the derived
 * `isMarkedReady`) so field-readiness flows through the single `Marked Ready`
 * source rather than the retired "Field Ready at Submission?" field.
 *
 * Returns a new job (input not mutated); the caller supplies `markedReadyAt`
 * so the domain stays clock-free.
 */
export function applyReadyAtSubmission<T extends Partial<Job>>(job: T, markedReadyAt: Date): T {
	if (!job.isReadyToSample) return job;
	return { ...job, dateMarkedReady: markedReadyAt, isMarkedReady: true };
}

export function finalizeJobForSubmit<T extends Partial<Job>>(job: T): T {
	const result = { ...job };

	if (result.dealIds?.[0] === CUSTOM_DEAL_SENTINEL) {
		result.dealIds = [];
	}
	if (result.growerIds?.[0] === CREATE_NEW_GROWER) {
		result.growerIds = [];
	}
	if (result.importGrowerName?.startsWith(NEW_GROWER_NAME_PREFIX)) {
		result.importGrowerName = result.importGrowerName.slice(NEW_GROWER_NAME_PREFIX.length);
	}
	if (result.salespersonIds?.includes(CREATE_NEW_SALESPERSON)) {
		result.salespersonIds = [];
	}

	// Integrations can hand back geometry-less features (Agrian returned a
	// boundary parsing to a MultiPolygon with empty coordinates — the 2026-08-06
	// outage). `hasGeoJSON` already treats those as "no file" for validation;
	// prune them here so the payload agrees and the API's empty-geometry 422
	// can never fire on a portal-built submission. An all-empty collection
	// drops to undefined — a boundary-less job is a first-class concept
	// (the Collect Boundary workflow).
	if (result.files) {
		result.files = {
			...result.files,
			pointsGeoJSON: pruneEmptyGeometryFeatures(result.files.pointsGeoJSON),
			boundaryGeoJSON: pruneEmptyGeometryFeatures(result.files.boundaryGeoJSON),
			zonesGeoJSON: pruneEmptyGeometryFeatures(result.files.zonesGeoJSON),
		};
	}

	return result;
}
